import * as React from 'react';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';
import { AuthLink } from '@/features/auth/components/AuthLink';
import { api, getErrorMessage } from '@/api/api';
import { useToast } from '@/context/ToastContext';

interface VerificationPendingProps {
  email: string;
  onBackToLogin: () => void;
}

const COOLDOWN_SECONDS = 60;

export const VerificationPending: React.FC<VerificationPendingProps> = ({
  email,
  onBackToLogin,
}) => {
  const [isResending, setIsResending] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const { addToast } = useToast();

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || isResending) return;
    setIsResending(true);
    try {
      await api.auth.resendVerification(email);
      addToast({
        title: 'Email sent',
        subtitle: 'A new verification link has been sent to your email.',
        type: 'success',
      });
      setCooldown(COOLDOWN_SECONDS);
    } catch (err) {
      addToast({
        title: 'Failed to resend',
        subtitle: getErrorMessage(err),
        type: 'error',
      });
    } finally {
      setIsResending(false);
    }
  };

  const isDisabled = cooldown > 0 || isResending;

  return (
    <div className='flex size-full flex-col items-center justify-center px-6 py-10'>
      {/* Envelope icon */}
      <svg
        className={clsx('mb-6 size-16', 'text-white fold:text-brand-800')}
        xmlns='http://www.w3.org/2000/svg'
        fill='none'
        viewBox='0 0 24 24'
        strokeWidth={1.5}
        stroke='currentColor'
      >
        <path
          strokeLinecap='round'
          strokeLinejoin='round'
          d='M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75'
        />
      </svg>

      {/* Title */}
      <h2
        className={clsx(
          'font-proxima font-bold leading-25 tracking-normal',
          'mb-3 text-center text-[30px] text-white',
          'fold:text-[38px] fold:text-brand-800'
        )}
      >
        Check your email
      </h2>

      {/* Subtitle */}
      <p
        className={clsx(
          'font-sans font-normal leading-21 tracking-normal',
          'mb-1 text-center text-14 text-white',
          'fold:text-[18px] fold:text-brand-750'
        )}
      >
        We sent a verification link to
      </p>
      <p
        className={clsx(
          'font-sans font-bold leading-21 tracking-normal',
          'mb-8 text-center text-14 text-white',
          'fold:text-[18px] fold:text-brand-800'
        )}
      >
        {email}
      </p>

      {/* Resend button */}
      <div className='mb-6 flex w-full max-w-[385px] justify-center'>
        <Button
          type='button'
          variant='purple-dark'
          className={clsx(
            'h-[54px] w-full max-w-[385px]',
            'rounded-30 text-16 font-bold uppercase tracking-wide'
          )}
          disabled={isDisabled}
          isLoading={isResending}
          onClick={handleResend}
        >
          {cooldown > 0 ? `RESEND EMAIL (${cooldown}s)` : 'RESEND EMAIL'}
        </Button>
      </div>

      {/* Back to login */}
      <AuthLink text='' actionText='Back to login' onClick={onBackToLogin} variant='light' />
    </div>
  );
};
